// In-app purchase sheet: Stripe Embedded Checkout inside a GathrXR modal, so
// paying never leaves the app. `council` opens on the tier choice first.
// `custom` reuses the same sheet for anything with its own session endpoint
// (marketplace listings): it supplies the title and starts the session.
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Crown, Loader, Lock, X } from 'lucide-react';
import { paymentsEnabled, PRODUCT_LABELS, type ProductKey } from '../../config/payments';
import { createCheckoutSession, loadStripeJs, waitForPaid, type ConfirmResult, type EmbeddedSession } from '../../services/checkout';
import './CheckoutModal.css';

type Phase = 'choose' | 'loading' | 'form' | 'confirming' | 'paid' | 'error';
const COUNCIL: ProductKey[] = ['pass', 'pro', 'visionary'];

export interface CustomCheckout {
  title: string;
  blurb?: string;
  /** Creates the session server-side; throw with a user-facing message on failure. */
  start: () => Promise<EmbeddedSession>;
  /** Stripe reports the form complete. The caller confirms delivery itself. */
  onComplete: (sessionId: string) => void;
}

export const CheckoutModal: React.FC<{
  product?: ProductKey | 'council';
  custom?: CustomCheckout;
  returnPath?: string;
  onClose: () => void;
  onPaid?: (result: ConfirmResult, product: ProductKey) => void;
}> = ({ product = 'council', custom, returnPath = '/welcome', onClose, onPaid }) => {
  const [chosen, setChosen] = useState<ProductKey | 'custom' | null>(custom ? 'custom' : product === 'council' ? null : product);
  const [phase, setPhase] = useState<Phase>(product === 'council' ? 'choose' : 'loading');
  const [error, setError] = useState('');
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!chosen || !paymentsEnabled) return;
    let checkout: any = null;
    let cancelled = false;
    setPhase('loading');
    setError('');
    (async () => {
      try {
        // The session comes first: the server says which Stripe account (and
        // so which publishable key) it was created on, so the form can never
        // be initialised against a different account than the session.
        const session = chosen === 'custom' ? await custom!.start() : await createCheckoutSession(chosen, returnPath);
        const stripe = await loadStripeJs(session.publishableKey);
        if (cancelled) return;
        checkout = await stripe.initEmbeddedCheckout({
          fetchClientSecret: async () => session.clientSecret,
          onComplete: async () => {
            if (chosen === 'custom') { custom!.onComplete(session.sessionId); return; }
            setPhase('confirming');
            const result = await waitForPaid(session.sessionId);
            if (result.paid) {
              setPhase('paid');
              onPaid?.(result, chosen);
            } else {
              setError('Payment received by Stripe but not confirmed yet. It will appear shortly — you can close this.');
              setPhase('error');
            }
          },
        });
        if (cancelled) { checkout.destroy(); return; }
        setPhase('form');
        checkout.mount(mountRef.current!);
      } catch (err: any) {
        if (!cancelled) { setError(err?.message || 'Could not start checkout.'); setPhase('error'); }
      }
    })();
    return () => { cancelled = true; try { checkout?.destroy(); } catch { /* already gone */ } };
    // onPaid is a callback prop; re-running checkout when it changes would double-charge-risk the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosen, returnPath]);

  const busy = phase === 'confirming';
  const label = chosen === 'custom'
    ? { name: custom!.title, price: '', blurb: custom!.blurb || '' }
    : chosen ? PRODUCT_LABELS[chosen] : null;

  return createPortal(
    <div className="gx-checkout-overlay" onClick={() => !busy && onClose()}>
      <div className="gx-checkout" role="dialog" aria-modal="true" aria-label="Checkout" onClick={(e) => e.stopPropagation()}>
        <header className="gx-checkout-head">
          <div>
            <span className="gx-checkout-eyebrow"><Lock size={12} /> Secure checkout</span>
            <h3>{label ? (label.price ? `${label.name} · ${label.price}` : label.name) : 'Choose your Creator package'}</h3>
          </div>
          {!busy && <button className="gx-checkout-x" onClick={onClose} aria-label="Close"><X size={18} /></button>}
        </header>

        {!paymentsEnabled ? (
          <div className="gx-checkout-state">
            <p>Payments are unavailable right now. Please try again later.</p>
          </div>
        ) : phase === 'choose' ? (
          <div className="gx-checkout-tiers">
            {COUNCIL.map((k) => (
              <button key={k} className={`gx-tier ${k === 'pro' ? 'is-featured' : ''}`} onClick={() => setChosen(k)}>
                {k === 'pro' && <span className="gx-tier-flag">Most popular</span>}
                <Crown size={18} />
                <strong>{PRODUCT_LABELS[k].name}</strong>
                <span className="gx-tier-price">{PRODUCT_LABELS[k].price}<em> one-time</em></span>
                <span className="gx-tier-blurb">{PRODUCT_LABELS[k].blurb}</span>
              </button>
            ))}
            <a className="gx-checkout-more" href="/council" target="_blank" rel="noopener noreferrer">Compare everything on the Council page</a>
          </div>
        ) : (
          <>
            {label?.blurb && phase !== 'paid' && <p className="gx-checkout-blurb">{label.blurb}</p>}
            {phase === 'loading' && <div className="gx-checkout-state"><Loader size={22} className="spin" /> Loading secure form…</div>}
            {phase === 'confirming' && <div className="gx-checkout-state"><Loader size={22} className="spin" /> Confirming your payment…</div>}
            {phase === 'paid' && <div className="gx-checkout-state is-ok"><Check size={22} /> Payment complete — you're all set.</div>}
            {phase === 'error' && <div className="gx-checkout-state is-err">{error}</div>}
            <div ref={mountRef} className={`gx-checkout-mount ${phase === 'form' ? '' : 'is-hidden'}`} />
            {!custom && product === 'council' && phase !== 'paid' && !busy && (
              <button className="gx-checkout-back" onClick={() => { setChosen(null); setPhase('choose'); }}>← Other packages</button>
            )}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default CheckoutModal;
